/** 账单明细行：前后端字段映射与展示 */

export function preShipmentTagText (row) {
  const r = String(row?.line_status_reason || '')
  if (r.includes('预付')) return '预付'
  return '预请款'
}

export function formatLineItemNotes (row) {
  const parts = [row?.item_notes, row?.item_notes_2, row?.item_notes_3]
    .map((x) => String(x || '').trim())
    .filter(Boolean)
  if (parts.length) return parts.join(' | ')
  const n = String(row?.notes || '').trim()
  if (n && !/销售订单/.test(n)) return n
  return ''
}

function resolveLineQuantity (ln) {
  if (ln.line_quantity != null && ln.line_quantity !== '') return ln.line_quantity
  if (ln.source_line_type === 'sales_order_item_dye') return 1
  if (ln.source_line_type === 'purchase_order_item') {
    const rq = ln.received_quantity
    if (rq != null && rq !== '' && Number(rq) > 0) return rq
  }
  if (ln.quantity != null && ln.quantity !== '') return ln.quantity
  return null
}

function roundMoney2 (n) {
  return Math.round((Number(n) + Number.EPSILON) * 100) / 100
}

function roundUnitPrice4 (n) {
  return Math.round((Number(n) + Number.EPSILON) * 10000) / 10000
}

/** 金额÷数量 → 结算单价；无法计算时返回 null */
export function deriveSettlementUnitPrice (amount, qty) {
  const a = Number(amount)
  const q = Number(qty)
  if (!Number.isFinite(a) || !Number.isFinite(q) || q === 0) return null
  return roundUnitPrice4(a / q)
}

/**
 * 结算单价展示/编辑：账单明细不落库单价，以「金额÷数量」为准。
 * 来源单据单价常为 0（对账时才填价），若仍优先用源单价会把结算单价显示成 0。
 */
export function ensureLineUnitPrice (line) {
  if (!line || typeof line !== 'object') return line
  const derived = deriveSettlementUnitPrice(line.amount, line.line_quantity)
  if (derived != null) {
    line.unit_price = derived
    return line
  }
  const existing = Number(line.unit_price)
  if (line.unit_price != null && line.unit_price !== '' && Number.isFinite(existing) && existing > 0) {
    return line
  }
  if (line.unit_price === 0 || line.unit_price === '0') {
    line.unit_price = null
  }
  return line
}

/** 改单价 → 重算金额（数量×单价）；数量或单价未填时不改金额（避免期初等无数量行被清成 0） */
export function syncLineAmountFromUnitPrice (line) {
  if (!line || typeof line !== 'object') return line
  if (line.line_quantity == null || line.line_quantity === '') return line
  if (line.unit_price == null || line.unit_price === '') return line
  const q = Number(line.line_quantity)
  const p = Number(line.unit_price)
  if (!Number.isFinite(q) || !Number.isFinite(p)) return line
  line.amount = roundMoney2(q * p)
  return line
}

/** 改金额或数量 → 保留金额，重算单价 */
export function syncLineUnitPriceFromAmount (line) {
  if (!line || typeof line !== 'object') return line
  const derived = deriveSettlementUnitPrice(line.amount, line.line_quantity)
  line.unit_price = derived
  return line
}

function strField (v) {
  if (v == null) return ''
  return String(v).trim()
}

/** 从 API/行对象解析仓库料号（兼容 batch_no 等历史字段名） */
export function pickWarehouseManualCode (obj) {
  if (!obj) return ''
  return (
    strField(obj.warehouse_manual_code) ||
    strField(obj.batch_no) ||
    strField(obj.manual_batch_number) ||
    strField(obj.warehouse_batch_no)
  )
}

/** 将 API 原始补全字段覆盖到映射行（料号/颜色/内部材料等） */
export function applyStatementLineApiEnrichment (mapped, api) {
  if (!api || !mapped) return mapped
  const pick = (key) => {
    const fromApi = strField(api[key])
    if (fromApi) return fromApi
    return strField(mapped[key])
  }
  const wh = pickWarehouseManualCode(api) || pickWarehouseManualCode(mapped)
  return {
    ...mapped,
    warehouse_manual_code: wh,
    internal_material_name: pick('internal_material_name'),
    tax_classification_name: pick('tax_classification_name'),
    color_cn: pick('color_cn'),
    color_en: pick('color_en'),
    product_name: pick('product_name'),
    specification: pick('specification'),
    unit: pick('unit'),
    order_no: pick('order_no'),
    order_type_name: pick('order_type_name'),
    order_date: pick('order_date'),
    source_customer_name: pick('source_customer_name'),
    item_notes: pick('item_notes'),
    item_notes_2: pick('item_notes_2'),
    item_notes_3: pick('item_notes_3')
  }
}

export function defaultReconcileGroupBy (direction) {
  return direction === 'payable' ? 'none' : 'internal_material_name'
}

export function mapStatementLineFromApi (ln, direction) {
  const isProcessing = ln.source_line_type === 'processing_record'
  const isPurchase = ln.source_line_type === 'purchase_order_item'
  const isPayable =
    direction === 'payable' ||
    (direction == null && (isProcessing || isPurchase))
  const mapped = {
    line_id: ln.line_id,
    line_type: ln.line_type,
    source_type: ln.source_type,
    source_id: ln.source_id,
    source_line_type: ln.source_line_type,
    source_line_id: ln.source_line_id,
    source_line_no: ln.source_line_no,
    line_status: ln.line_status,
    line_status_reason: ln.line_status_reason,
    delivery_order_no: ln.delivery_order_no,
    biz_date: ln.biz_date,
    doc_no: ln.doc_no,
    summary: ln.summary,
    amount: ln.amount,
    adjust_category: ln.adjust_category || '',
    line_notes: ln.line_notes || '',
    line_quantity: resolveLineQuantity(ln),
    client_style_no: ln.client_style_no || '',
    client_instruct_no: ln.client_instruct_no || '',
    client_material_name: isProcessing || isPayable ? '' : (ln.client_material_name || ''),
    product_name: isPayable
      ? (ln.product_name || '')
      : (isProcessing ? (ln.product_name || '') : (ln.client_material_name || ln.product_name || '')),
    warehouse_manual_code: pickWarehouseManualCode(ln),
    specification: ln.specification || '',
    color_en: isPayable ? (ln.color_en || '') : (ln.color_en || ln.client_color_en || ''),
    color_cn: isPayable ? (ln.color_cn || '') : (ln.color_cn || ln.client_color_cn || ''),
    client_color_en: ln.client_color_en || '',
    client_color_cn: ln.client_color_cn || '',
    unit: ln.unit || '',
    order_no: ln.order_no || '',
    order_date: ln.order_date || '',
    client_purchase_order_no: ln.client_purchase_order_no || '',
    order_type_name: ln.order_type_name || '',
    internal_material_name: ln.internal_material_name || '',
    tax_classification_name: ln.tax_classification_name || '',
    source_customer_name: ln.source_customer_name || '',
    item_notes: ln.item_notes || '',
    item_notes_2: ln.item_notes_2 || '',
    item_notes_3: ln.item_notes_3 || '',
    notes: ln.notes || '',
    unit_price: ln.unit_price ?? null,
    dye_fee: ln.dye_fee ?? null,
    line_amount: ln.line_amount ?? ln.amount ?? null
  }
  return ensureLineUnitPrice(mapped)
}

export function formatMoney (v) {
  if (v == null || v === '') return '—'
  const n = Number(v)
  if (Number.isNaN(n)) return '—'
  return n.toLocaleString('zh-CN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

export function formatLineQty (v) {
  if (v == null || v === '') return '—'
  const n = Number(v)
  if (Number.isNaN(n)) return '—'
  return n.toLocaleString('zh-CN', { maximumFractionDigits: 4 })
}

export function sourceLineTypeLabel (slt) {
  const map = {
    sales_order_item: '订单项',
    sales_order_item_material: '材料',
    sales_order_item_dye: '染费',
    purchase_order_item: '采购项',
    processing_record: '加工单',
    delivery_order_item: '出库项',
    customer_return_item: '客户退货',
    inventory_transaction: '入库流水'
  }
  return map[slt] || slt || '—'
}

/** 明细行唯一来源键（用于去重） */
export function statementLineSourceKey (ln) {
  if (!ln) return ''
  const slt = ln.source_line_type || 'manual'
  const sid = ln.source_line_id
  if (sid != null && sid !== '') {
    const n = Number(sid)
    const idPart = Number.isInteger(n) && n > 0 ? n : sid
    // 材料/染费拆分时 source_line_id 是订单项次，多笔出库会撞键；带上出库单号区分
    const doNo = String(ln.delivery_order_no || '').trim()
    if (
      doNo &&
      (slt === 'sales_order_item' ||
        slt === 'sales_order_item_material' ||
        slt === 'sales_order_item_dye')
    ) {
      return `${slt}:${idPart}:do:${doNo}`
    }
    return `${slt}:${idPart}`
  }
  if (ln.line_type === 'charge') {
    const doc = String(ln.doc_no || '').trim()
    const sum = String(ln.summary || '').trim()
    const doNo = String(ln.delivery_order_no || '').trim()
    if (doc) return doNo ? `doc:${doc}:${doNo}:${sum}` : `doc:${doc}:${sum}`
  }
  return ''
}

/** 打开明细核对 / 编辑加载：把 API 补全的来源字段写回当前行（按 line_id 或来源键匹配） */
export function mergeReconcileMetaFromApiLines (existingLines, apiLines) {
  const byLineId = new Map()
  const bySource = new Map()
  for (const ln of apiLines || []) {
    const lid = Number(ln.line_id)
    if (Number.isInteger(lid) && lid > 0) byLineId.set(lid, ln)
    const k = statementLineSourceKey(ln)
    if (k) bySource.set(k, ln)
  }
  const enrichFields = [
    'order_no',
    'order_date',
    'order_type_name',
    'internal_material_name',
    'tax_classification_name',
    'source_customer_name',
    'product_name',
    'warehouse_manual_code',
    'specification',
    'color_en',
    'color_cn',
    'unit',
    'item_notes',
    'item_notes_2',
    'item_notes_3',
    'received_quantity',
    'line_quantity'
  ]
  return (existingLines || []).map((ln) => {
    const lid = Number(ln.line_id)
    const api =
      (Number.isInteger(lid) && lid > 0 && byLineId.get(lid)) ||
      bySource.get(statementLineSourceKey(ln))
    if (!api) return ln
    const merged = { ...ln }
    for (const f of enrichFields) {
      const v = api[f]
      if (v != null && v !== '') merged[f] = v
    }
    return merged
  })
}

/** 用 API 补全行：有本地行时以本地为准只 enrichment；无本地行时用 API 全量（打开账单详情） */
export function mergeStatementLinesFromApiDetail (userLines, apiLines, { direction } = {}) {
  const apiMapped = (apiLines || []).map((api) =>
    applyStatementLineApiEnrichment(mapStatementLineFromApi(api, direction), api)
  )
  let apiOut = apiMapped
  if (direction === 'receivable') {
    // 应收：本期增加 / 调整 / 期初余额（建期初账单）
    apiOut = apiOut.filter(
      (ln) => ln.line_type === 'charge' || ln.line_type === 'adjust' || ln.line_type === 'opening'
    )
  }

  const user = userLines || []
  if (!user.length) {
    return dedupeStatementLinesBySource(apiOut)
  }

  const apiByLineId = new Map()
  const apiBySource = new Map()
  for (const ln of apiOut) {
    const lid = Number(ln.line_id)
    if (Number.isInteger(lid) && lid > 0) apiByLineId.set(lid, ln)
    const sk = statementLineSourceKey(ln)
    if (sk) apiBySource.set(sk, ln)
  }

  const out = user.map((userLn) => {
    const lid = Number(userLn.line_id)
    const apiRow =
      (Number.isInteger(lid) && lid > 0 && apiByLineId.get(lid)) ||
      apiBySource.get(statementLineSourceKey(userLn))
    if (!apiRow) return syncLineUnitPriceFromAmount({ ...userLn })
    return syncLineUnitPriceFromAmount({
      ...apiRow,
      line_id: userLn.line_id ?? apiRow.line_id,
      amount: userLn.amount,
      line_quantity: userLn.line_quantity,
      line_notes: userLn.line_notes ?? apiRow.line_notes,
      biz_date: userLn.biz_date ?? apiRow.biz_date,
      doc_no: userLn.doc_no ?? apiRow.doc_no,
      summary: userLn.summary ?? apiRow.summary,
      adjust_category: userLn.adjust_category ?? apiRow.adjust_category,
      // 保留草稿来源键，避免被 API 行盖成相同键后再去重丢行
      source_line_type: userLn.source_line_type ?? apiRow.source_line_type,
      source_line_id: userLn.source_line_id ?? apiRow.source_line_id,
      delivery_order_no: userLn.delivery_order_no ?? apiRow.delivery_order_no
    })
  })

  // 有本地草稿时保持行数，不再二次去重（去重会把未保存新行挤掉）
  return out
}

/** 替换自动带出的 charge 行时保留：期初/收付/调整 + 手工行 */
export function preserveLinesOnGenerateReplace (lines) {
  const preservedTypes = new Set(['opening', 'payment', 'adjust'])
  return (lines || []).filter((ln) => {
    if (preservedTypes.has(ln.line_type)) return true
    if (ln.line_type === 'charge' && (ln.source_type === 'manual' || !ln.source_line_id)) return true
    return false
  })
}

/** 按来源键去重，保留后出现的行（新生成覆盖旧自动行） */
export function dedupeStatementLinesBySource (lines) {
  const keyed = new Map()
  const tail = []
  for (const ln of lines || []) {
    const key = statementLineSourceKey(ln)
    if (!key) {
      tail.push(ln)
      continue
    }
    keyed.set(key, ln)
  }
  const out = []
  const used = new Set()
  for (const ln of lines || []) {
    const key = statementLineSourceKey(ln)
    if (!key) continue
    if (used.has(key)) continue
    used.add(key)
    out.push(keyed.get(key))
  }
  return [...out, ...tail]
}

function isFilledMoney (v) {
  const n = Number(v)
  return v != null && v !== '' && Number.isFinite(n) && Math.abs(n) > 0.0000001
}

/** 应付重新取数：替换已有自动 charge 行，保留手工/期初/收付/调整；
 * 同源键自动行带回已填单价/金额/备注；新行保持取数原样（多为空待填）。 */
export function mergePayableGeneratedLines (existingLines, generatedLines) {
  const preserved = preserveLinesOnGenerateReplace(existingLines)
  const existingByKey = new Map()
  for (const ln of existingLines || []) {
    if ((ln.line_type || 'charge') !== 'charge') continue
    if (ln.source_type === 'manual' || !ln.source_line_id) continue
    const k = statementLineSourceKey(ln)
    if (k) existingByKey.set(k, ln)
  }
  const mergedGen = (generatedLines || []).map((gen) => {
    const k = statementLineSourceKey(gen)
    const old = k ? existingByKey.get(k) : null
    if (!old) {
      // 全新拉入的行：不沿用任何旧价，取数为空则保持空
      const fresh = { ...gen }
      if (!isFilledMoney(fresh.amount) && !isFilledMoney(fresh.unit_price)) {
        fresh.amount = fresh.amount == null || fresh.amount === '' ? null : fresh.amount
        fresh.unit_price = fresh.unit_price == null || fresh.unit_price === '' ? null : fresh.unit_price
        return fresh
      }
      return syncLineUnitPriceFromAmount(fresh)
    }
    const out = { ...gen }
    if (old.line_id != null && old.line_id !== '') out.line_id = old.line_id

    const oldAmtFilled = isFilledMoney(old.amount)
    const oldPriceFilled = isFilledMoney(old.unit_price)
    // 已填金额/单价一律保留（手工对账改价优先于采购/加工带出的价）
    if (oldAmtFilled) out.amount = old.amount
    if (oldPriceFilled) out.unit_price = old.unit_price

    if (old.line_notes != null && String(old.line_notes).trim() !== '') {
      out.line_notes = old.line_notes
    }
    if (old.summary != null && String(old.summary).trim() !== '' && !(gen.summary || '').trim()) {
      out.summary = old.summary
    }

    // 只填了金额 → 反算单价；只填了单价且金额仍空 → 按数量算金额
    if (oldAmtFilled && !oldPriceFilled) {
      return syncLineUnitPriceFromAmount(out)
    }
    if (oldPriceFilled && !oldAmtFilled) {
      return syncLineAmountFromUnitPrice(out)
    }
    return ensureLineUnitPrice(out)
  })
  return dedupeStatementLinesBySource([...preserved, ...mergedGen])
}

export function computeReceivableLineTotal (lines) {
  let total = 0
  for (const ln of lines || []) {
    total += lineSignedAmountForReceivable(ln)
  }
  return Math.round(total * 100) / 100
}

export function computeChargeLineTotal (lines) {
  let total = 0
  for (const ln of lines || []) {
    if (ln.line_type !== 'charge') continue
    const amt = Number(ln.amount)
    if (Number.isFinite(amt)) total += amt
  }
  return Math.round(total * 100) / 100
}

/** 应收明细行对合计的带符号贡献 */
export function lineSignedAmountForReceivable (row) {
  if (!row) return 0
  const raw = Number(row.amount)
  if (!Number.isFinite(raw)) return 0
  const absAmt = Math.abs(raw)
  const lt = row.line_type || 'charge'
  if (lt === 'opening') return absAmt
  if (lt === 'charge') return raw
  if (lt === 'adjust') {
    const cat = row.adjust_category || 'general'
    if (cat === 'shortage_deduct' || cat === 'quality_deduct' || cat === 'manual_deduct') return -absAmt
    return raw
  }
  return 0
}

/** 应付明细行对合计的带符号贡献（用于表尾/预览） */
export function lineSignedAmountForPayable (row) {
  if (!row) return 0
  const raw = Number(row.amount)
  if (!Number.isFinite(raw)) return 0
  const absAmt = Math.abs(raw)
  const lt = row.line_type || 'charge'
  if (lt === 'charge' || lt === 'opening') return absAmt
  if (lt === 'payment') return -absAmt
  if (lt === 'adjust') {
    const cat = row.adjust_category || 'general'
    if (cat === 'shortage_deduct' || cat === 'quality_deduct' || cat === 'manual_deduct') return -absAmt
    return raw
  }
  return raw
}

export function computeRunningStatementTotal (lines, direction = 'receivable', reconciliation = null) {
  if (direction === 'payable') {
    return computePayableReconciliation(lines, reconciliation).system_total
  }
  return computeReceivableLineTotal(lines)
}

/** 应付：明细不含税；扣款来自 adjust 行；含税=不含税/(1-税点%)；税额=含税×税点% */
export function computePayableReconciliation (lines, reconciliation = {}) {
  const chargeTotal = computeChargeLineTotal(lines)
  let openingTotal = 0
  let generalAdjust = 0
  let paymentTotal = 0
  let shortage = 0
  let quality = 0
  for (const ln of lines || []) {
    const amt = Number(ln.amount) || 0
    if (ln.line_type === 'opening') openingTotal += amt
    else if (ln.line_type === 'payment') paymentTotal += amt
    else if (ln.line_type === 'adjust') {
      const cat = ln.adjust_category || 'general'
      const absAmt = Math.abs(amt)
      if (cat === 'shortage_deduct') shortage += absAmt
      else if (cat === 'quality_deduct') quality += absAmt
      else generalAdjust += amt
    }
  }
  openingTotal = Math.round(openingTotal * 100) / 100
  generalAdjust = Math.round(generalAdjust * 100) / 100
  paymentTotal = Math.round(paymentTotal * 100) / 100
  shortage = Math.round(shortage * 100) / 100
  quality = Math.round(quality * 100) / 100
  const deductTotal = Math.round((shortage + quality) * 100) / 100
  const rate = Math.max(0, Math.min(99.9999, Number(reconciliation?.payable_tax_rate_pct) || 0))
  const rateDec = rate / 100

  const pretaxTotal = Math.round((chargeTotal + openingTotal + generalAdjust - deductTotal) * 100) / 100
  const pretaxBase = Math.max(0, pretaxTotal)
  let grossInclusive = pretaxBase
  let taxAmount = 0
  if (rateDec > 0 && rateDec < 1 && pretaxBase > 0) {
    grossInclusive = Math.round(pretaxBase / (1 - rateDec) * 100) / 100
    taxAmount = Math.round(grossInclusive * rateDec * 100) / 100
  }

  const systemTotal = Math.round((grossInclusive - paymentTotal) * 100) / 100

  return {
    charge_total: chargeTotal,
    opening_total: openingTotal,
    adjust_total: generalAdjust,
    payment_total: paymentTotal,
    payable_shortage_deduct: shortage,
    payable_quality_deduct: quality,
    payable_deduct_total: deductTotal,
    payable_tax_rate_pct: rate,
    payable_pretax_total: pretaxBase,
    payable_gross_total: grossInclusive,
    payable_tax_amount: taxAmount,
    system_total: systemTotal
  }
}

/** 应付列表行：按税点重算税额/含税（忽略库内 stale 0） */
export function resolvePayableListRowAmounts (row) {
  if (!row || row.direction !== 'payable') return row
  const rate = Number(row.payable_tax_rate_pct) || 0
  let pretax = Number(row.payable_pretax_total)
  if (!Number.isFinite(pretax) || pretax <= 0) {
    const charge = Number(row.charge_total)
    pretax = Number.isFinite(charge) ? charge : 0
  }
  const rateDec = rate / 100
  let grossInclusive = pretax
  let taxAmount = 0
  if (pretax > 0 && rateDec > 0 && rateDec < 1) {
    grossInclusive = Math.round(pretax / (1 - rateDec) * 100) / 100
    taxAmount = Math.round(grossInclusive * rateDec * 100) / 100
  }
  const systemTotal = Number(row.system_total)
  const grossFallback =
    Number.isFinite(systemTotal) && systemTotal > 0 ? systemTotal : grossInclusive
  if (rateDec > 0 && pretax > 0) {
    return {
      ...row,
      payable_pretax_total: pretax,
      payable_tax_amount: taxAmount,
      payable_gross_total: grossFallback > 0 ? grossFallback : grossInclusive
    }
  }
  return {
    ...row,
    payable_gross_total:
      row.payable_gross_total != null && Number(row.payable_gross_total) > 0
        ? row.payable_gross_total
        : grossFallback
  }
}

export function isManualReceivableAdjustmentLine (ln) {
  return ln?.line_type === 'adjust' && (ln.source_type === 'manual' || !ln.source_line_id)
}

export function isManualChargeLine (ln) {
  return ln?.line_type === 'charge' && (ln.source_type === 'manual' || !ln.source_line_id)
}

/** 应收：取数结果覆盖自动明细，保留手工行；取数侧已去重，此处仅去掉与新行同源的旧手工冲突 */
export function mergeReceivableGeneratedLines (existingLines, generatedLines) {
  const manual = (existingLines || []).filter(
    (ln) => isManualChargeLine(ln) || isManualReceivableAdjustmentLine(ln)
  )
  const gen = Array.isArray(generatedLines) ? generatedLines.slice() : []
  const genKeys = new Set()
  for (const ln of gen) {
    const k = statementLineSourceKey(ln)
    if (k) genKeys.add(k)
  }
  const manualKept = manual.filter((ln) => {
    const k = statementLineSourceKey(ln)
    return !k || !genKeys.has(k)
  })
  return [...manualKept, ...gen]
}
